// ============================================================================
// LES PÉRIODES D'UNE AUDIENCE — « les 12 derniers mois », « il y a 6 mois », en jours du calendrier.
//
// ⚠️ PUR ET SANS DÉPENDANCE, parce que DEUX CÔTÉS le lisent : le serveur, qui résout la question le jour où elle
// sert, et l'écran de l'assistant, qui montre la même fenêtre au moment où on la compose (« 12 derniers mois ·
// 29/10/2025 → 28/10/2026 »). Deux calculs écrits séparément annonceraient une fenêtre et en appliqueraient une
// autre — un jour de décalage sur un 31 suffit.
// ⚠️ IL NE CONNAÎT PAS LE FUSEAU : il travaille sur un jour `AAAAMMJJ` déjà lu dans le bon calendrier. C'est
// l'appelant qui décide quel est « aujourd'hui » (le serveur : dans le fuseau du magasin).
// ============================================================================

/**
 * LES DURÉES RELATIVES qu'un critère daté accepte — « il y a N mois », recalculé à chaque usage.
 *
 * ⚠️ 🔴 UNE AUDIENCE ENREGISTRÉE OU UN MODÈLE RÉCURRENT GARDE LA DURÉE, jamais la date qu'elle donnait le
 * jour de la saisie : « pas venus depuis 6 mois » figé au 28 octobre viserait encore, en mars, les gens
 * absents depuis le 28 avril de l'an passé — et l'écran afficherait des envois parfaitement réussis.
 */
export const RELATIVE_SPANS = ['1m', '3m', '6m', '12m', '24m'] as const;
export type RelativeSpan = (typeof RELATIVE_SPANS)[number];

/** La PÉRIODE OBSERVÉE en relatif : les N derniers mois, cette année (`ytd`), l'année dernière (`ly`). */
export const PURCHASE_PERIODS = [...RELATIVE_SPANS, 'ytd', 'ly'] as const;
export type PurchasePeriod = (typeof PURCHASE_PERIODS)[number];

/** Le nombre de mois d'une durée relative. */
export const spanMonths = (s: RelativeSpan): number => Number(s.slice(0, -1));

/**
 * Le même quantième N mois plus tôt — ramené au dernier jour du mois quand il n'y existe pas.
 * ⚠️ « Il y a un mois » le 31 mars est le 28 (ou 29) février, pas le 3 mars : un calcul qui déborde sur le mois
 *    suivant ferait commencer « le dernier mois » trois jours trop tard.
 */
export function monthsBefore(ymd: string, n: number): string {
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(4, 6));
  const d = Number(ymd.slice(6, 8));
  const total = y * 12 + (m - 1) - n;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12 + 1;
  const dernier = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return `${ny}${String(nm).padStart(2, '0')}${String(Math.min(d, dernier)).padStart(2, '0')}`;
}

/** Le lendemain d'un jour `AAAAMMJJ`. */
export function nextDay(ymd: string): string {
  const t = Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8))) + 86_400_000;
  const d = new Date(t);
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * LA PÉRIODE OBSERVÉE d'un raccourci, en jours du magasin, bornes incluses.
 * ⚠️ « Les 12 derniers mois » finit AUJOURD'HUI et commence le lendemain du même jour un an plus tôt : douze
 *    mois exactement, sans compter deux fois le jour anniversaire.
 */
export function purchaseWindowOf(p: PurchasePeriod, today: string): { from: string; to: string } {
  const annee = Number(today.slice(0, 4));
  if (p === 'ytd') return { from: `${annee}0101`, to: today };
  if (p === 'ly') return { from: `${annee - 1}0101`, to: `${annee - 1}1231` };
  return { from: nextDay(monthsBefore(today, spanMonths(p))), to: today };
}
